import { SoftwareSeatPermissions } from '@zspace/roles';
import {
  AvailabilityStatus,
  DeferredResponse,
  InputType,
  LicenseKeysCriteria,
  LicenseKeysExpandableTableRow,
  LicenseKeysTableRow,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SoftwareData,
  SortDirection,
} from '@zspace/types';
import { AxiosError } from 'axios';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Columns, Element } from 'react-bulma-components';
import { FaBolt, FaUser } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useParams,
  useRevalidator,
  useSearchParams,
} from 'react-router-dom';
import SalesOrdersList from '../../sales-orders/sales-orders-list/sales-orders-list';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useError from '../../shared/hooks/error';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsZspaceInternalUser from '../../shared/hooks/is-zspace-internal-user';
import useToast from '../../shared/hooks/toasts';
import useUserHasZspaceStaffPermissions from '../../shared/hooks/user-has-zspace-staff-permissions';
import ProtectedPage from '../../shared/protected-page/protected-page';
import {
  getSearchParamValue,
  getSearchParamValues,
  replaceUrlWithParams,
} from '../../shared/url';
import { unassignSoftwareFromDevice } from '../../software-assignment/software-assignment-service';
import BoxLayout from '../../ui/box-layout/box-layout';
import DetailsCard from '../../ui/details-card/details-card';
import InfoLabel from '../../ui/info-label/info-label';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import LicenseKeysTable from '../license-keys-table/license-keys-table';
import {
  fetchSoftwareDetails,
  fetchSoftwareRelatedSeats,
} from '../software-service';
import styles from './software-details-page.module.scss';

const SUCCESSFULLY_UNASSIGNED_SOFTWARE_MESSAGE =
  'License key successfully unassigned from device';

export const loader: LoaderFunction = async ({ params }) => {
  const id = params.id;

  const response = fetchSoftwareDetails(id!);

  return defer({ response });
};

const initialCriteria: (
  searchParams?: URLSearchParams
) => Omit<LicenseKeysCriteria, 'licensingProductGroupId'> = (searchParams?) => {
  return {
    itemsPerPage: parseInt(
      getSearchParamValue(searchParams, 'itemsPerPage') ?? '50'
    ),
    pageNumber: parseInt(
      getSearchParamValue(searchParams, 'pageNumber') ?? '1'
    ),
    search: getSearchParamValue(searchParams, 'search') ?? '',
    sortBy: getSearchParamValue(searchParams, 'sortBy') ?? '',
    sortDirection:
      getSearchParamValue<SortDirection>(searchParams, 'sortDirection') ??
      SortDirection.ASC,
    availabilityStatus:
      getSearchParamValue<AvailabilityStatus>(
        searchParams,
        'availabilityStatus'
      ) ?? AvailabilityStatus.AVAILABLE,
    licenseKeys: getSearchParamValues(searchParams, 'licenseKeys'),
    salesOrders: getSearchParamValues(searchParams, 'salesOrders'),
    endUsers: getSearchParamValues(searchParams, 'endUsers'),
    includeUnrelated:
      getSearchParamValue(searchParams, 'includeUnrelated') === 'true',
  };
};

export function SoftwareDetailsPageContent() {
  const isZspaceInternalUser = useIsZspaceInternalUser();
  const hasZspaceStaffPermissions = useUserHasZspaceStaffPermissions();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const revalidator = useRevalidator();
  const { setError } = useError();
  const softwareData = useAsyncValue() as SoftwareData;
  const {
    executeHttpRequest: executeFetchSoftwareRelatedSeats,
    isLoading: isLoadingSoftwareSeats,
  } = useHttpRequest();
  const { executeHttpRequest: executeUnassignSoftware } = useHttpRequest();
  const [relatedSoftwareSeats, setRelatedSoftwareSeats] = useState<
    PaginatedAPIResponse<LicenseKeysTableRow>
  >({
    data: [],
    count: 0,
    page: 0,
    total: 0,
    pages: 0,
  });
  const [filterCriteria, setFilterCriteria] = useState<LicenseKeysCriteria>(
    initialCriteria(searchParams)
  );

  const fetchRelatedSoftwareSeats = useCallback(async () => {
    return executeFetchSoftwareRelatedSeats({
      asyncFunction: async () => {
        const seats = await fetchSoftwareRelatedSeats(
          params.id!,
          filterCriteria
        );

        replaceUrlWithParams(
          `/software/${params.id}`,
          filterCriteria as unknown as Record<string, string>
        );
        setRelatedSoftwareSeats(seats);
      },
      customErrorHandler: (error: AxiosError) => {
        setError(error.message);
      },
    });
  }, [executeFetchSoftwareRelatedSeats, params.id, filterCriteria, setError]);

  const tableData: LicenseKeysTableRow[] = useMemo(
    () => relatedSoftwareSeats.data,
    [relatedSoftwareSeats.data]
  );

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({ ...filterCriteria, pages: relatedSoftwareSeats.pages }),
    [filterCriteria, relatedSoftwareSeats.pages]
  );

  const handleTableChange = useCallback(
    (
      value: OnTableChangeData<
        LicenseKeysTableRow,
        LicenseKeysExpandableTableRow
      >
    ) => {
      const newCriteria: LicenseKeysCriteria = {
        ...filterCriteria,
        ...value.config,
        sortBy: value.column?.key || filterCriteria.sortBy,
      };

      setFilterCriteria(newCriteria);
      fetchSoftwareRelatedSeats(params.id!, newCriteria);
    },
    [filterCriteria, params.id]
  );

  const handleUnassignClick = useCallback(
    async (deviceId: string) => {
      return executeUnassignSoftware({
        asyncFunction: async () => {
          const softwareId = params.id!;
          await unassignSoftwareFromDevice(deviceId, softwareId);

          revalidator.revalidate();
          await fetchRelatedSoftwareSeats();
          toast.success(SUCCESSFULLY_UNASSIGNED_SOFTWARE_MESSAGE);
        },
        customErrorHandler: (error: AxiosError) => {
          setError(error.message);
        },
      });
    },
    [
      executeUnassignSoftware,
      fetchRelatedSoftwareSeats,
      params.id,
      revalidator,
      setError,
      toast,
    ]
  );

  useEffect(() => {
    fetchRelatedSoftwareSeats();
  }, [fetchRelatedSoftwareSeats]);

  const handleTableFilterSubmit = useCallback(
    (value: Omit<LicenseKeysCriteria, 'licensingProductGroupId'>) => {
      const { itemsPerPage, search, sortBy, sortDirection } = filterCriteria;
      const newCriteria: LicenseKeysCriteria = {
        ...value,
        itemsPerPage,
        search,
        sortBy,
        sortDirection,
        pageNumber: 1,
      };
      setFilterCriteria(newCriteria);
      fetchSoftwareRelatedSeats(params.id!, newCriteria);
    },
    [filterCriteria, params.id]
  );

  const filtersData = useMemo(() => {
    return {
      licenseKeys: {
        title: 'License key',
        appliedCount: filterCriteria.licenseKeys?.length,
        value: filterCriteria.licenseKeys,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'licenseKeys',
          valueKey: 'licenseKeys',
          placeholder: 'Search by License key',
        },
      },
      salesOrders: {
        title: 'Sales order',
        appliedCount: filterCriteria.salesOrders?.length,
        value: filterCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      endUsers: {
        title: 'End user',
        appliedCount: filterCriteria.endUsers?.length,
        value: filterCriteria.endUsers,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'endUsers',
          valueKey: 'endUsers',
          placeholder: 'Search by End user',
        },
      },
    };
  }, [
    filterCriteria.endUsers,
    filterCriteria.licenseKeys,
    filterCriteria.salesOrders,
  ]) as FiltersData<LicenseKeysCriteria>;

  const customizeViewOptions = useMemo(() => {
    const options = [
      {
        title: 'Show keys with no available seats',
        applied: filterCriteria.availabilityStatus === AvailabilityStatus.ALL,
        valueKey: 'availabilityStatus',
        checkedValue: AvailabilityStatus.ALL,
        uncheckedValue: AvailabilityStatus.AVAILABLE,
      },
    ];
    if (isZspaceInternalUser && hasZspaceStaffPermissions) {
      return [
        {
          title: 'Show keys unrelated to me',
          applied: !!filterCriteria.includeUnrelated,
          valueKey: 'includeUnrelated',
        },
        ...options,
      ];
    }
    return options;
  }, [
    filterCriteria.availabilityStatus,
    filterCriteria.includeUnrelated,
    hasZspaceStaffPermissions,
    isZspaceInternalUser,
  ]);

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const onRemoveFilters = useCallback(
    (
      filterDataKeys: (keyof Omit<
        LicenseKeysCriteria,
        'licensingProductGroupId'
      >)[]
    ) => {
      const newData = { ...filterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria()[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [filterCriteria, handleTableFilterSubmit]
  );

  const emptyContent = useMemo(() => {
    const emptyContentText = appliedFilters.length
      ? 'There are no software seats that match the selected criteria'
      : 'No software seats have been registered yet';
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span>{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters.length]);

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Element className="p-4">
          <BackButton />
          <h1 className="is-size-3 has-text-weight-light">
            {softwareData.displayName}
          </h1>
          <h2 className="is-size-5 has-text-weight-light">
            <SalesOrdersList
              list={softwareData.salesOrders}
              textColor="primary"
            />
          </h2>
        </Element>
      }
    >
      <Columns className="p-4">
        <Columns
          display="flex"
          flexDirection="column"
          className={`pr-7 ${styles.informationColumn}`}
        >
          <Columns.Column>
            <DetailsCard title="Basic information">
              <InfoLabel
                icon={<FaBolt />}
                label="Software title"
                content={
                  <span className={styles.content}>
                    {softwareData.displayName}
                  </span>
                }
              />
              <InfoLabel
                icon={<FaUser />}
                label="Total seats"
                content={
                  <span className={styles.content}>
                    {softwareData.seats.total}
                  </span>
                }
              />
              <InfoLabel
                icon={<FaUser />}
                label="Available seats"
                content={
                  <span className={styles.content}>
                    {softwareData.seats.available}
                  </span>
                }
              />
            </DetailsCard>
          </Columns.Column>
        </Columns>
        <Columns.Column className={`pt-0 ${styles.tablesColumn}`}>
          <Conditional condition={isLoadingSoftwareSeats}>
            <Conditional.True>
              <PageSpinner />
            </Conditional.True>
            <Conditional.False>
              <Element
                display="flex"
                className="w-full pb-2"
                alignItems="center"
              >
                <FilterBar
                  initialData={initialCriteria()}
                  data={filterCriteria}
                  filtersData={filtersData}
                  customizeViewOptions={customizeViewOptions}
                  onFilterSubmit={handleTableFilterSubmit}
                  onRemoveFilter={onRemoveFilters}
                />
              </Element>
              <LicenseKeysTable
                dataSource={tableData}
                onChange={handleTableChange}
                onUnassign={handleUnassignClick}
                config={tableConfig}
                empty={emptyContent}
                form={{
                  enableSearch: false,
                  disableItemsPerPageSelection: true,
                }}
              />
            </Conditional.False>
          </Conditional>
        </Columns.Column>
      </Columns>
    </BoxLayout>
  );
}

export function SoftwareDetailsPage() {
  const { response } = useLoaderData() as DeferredResponse<SoftwareData>;

  return (
    <ProtectedPage permissions={SoftwareSeatPermissions.SOFTWARE_SEATS_READ}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <SoftwareDetailsPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default SoftwareDetailsPage;
